import { useState } from 'react'
import { Clock, Copy, Check, ChevronRight } from 'lucide-react'
import { quickStartSteps } from '@/data/reportContent'
import Button from '@/components/Button'

const QuickStart = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const [copiedCode, setCopiedCode] = useState('')

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(''), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  const nextStep = () => {
    if (currentStep < quickStartSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Quick Start Guide
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Get up and running with GitHub Copilot Agent in just a few simple steps. 
            Start building AI-powered applications today.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Step {currentStep + 1} of {quickStartSteps.length}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {Math.round(((currentStep + 1) / quickStartSteps.length) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-primary-500 to-accent-500 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((currentStep + 1) / quickStartSteps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Steps Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
          {quickStartSteps.map((step, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                index === currentStep
                  ? 'border-primary-800 bg-primary-200 dark:bg-primary-700/60 shadow-xl' // Current step: Much stronger contrast
                  : index < currentStep
                  ? 'border-green-700 bg-green-200 dark:bg-green-700/60 shadow-lg' // Completed: Much stronger contrast
                  : 'border-gray-400 dark:border-gray-500 bg-gray-100 dark:bg-gray-600 hover:border-gray-500 dark:hover:border-gray-400 hover:shadow-md' // Future: Much stronger inactive state
              }`}
              onClick={() => setCurrentStep(index)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${
                  index === currentStep
                    ? 'bg-primary-800 text-white dark:bg-primary-500 dark:text-white shadow-sm' // Current: Maximum contrast
                    : index < currentStep
                    ? 'bg-green-800 text-white dark:bg-green-500 dark:text-white shadow-sm' // Completed: Maximum contrast
                    : 'bg-gray-600 text-white dark:bg-gray-500 dark:text-white' // Future: Strong contrast
                }`}>
                  Step {index + 1}
                </span>
                <div className="flex items-center text-xs text-gray-700 dark:text-gray-300">
                  <Clock className="w-3 h-3 mr-1" />
                  {step.time}
                </div>
              </div>
              <h3 className={`font-semibold text-sm ${
                index === currentStep
                  ? 'text-primary-900 dark:text-primary-100' // Current: Maximum contrast
                  : index < currentStep
                  ? 'text-green-900 dark:text-green-100' // Completed: Maximum contrast
                  : 'text-gray-900 dark:text-gray-100' // Future: Maximum contrast
              }`}>
                {step.title}
              </h3>
            </div>
          ))}
        </div>

        {/* Current Step Detail */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {quickStartSteps[currentStep].title}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {quickStartSteps[currentStep].description}
              </p>
            </div>
            <div className="flex items-center text-sm text-gray-700 dark:text-gray-300 ml-4">
              <Clock className="w-4 h-4 mr-1" />
              {quickStartSteps[currentStep].time}
            </div>
          </div>

          {/* Code Block */}
          <div className="relative mb-6">
            <div className="flex items-center justify-between bg-gray-800 text-gray-300 px-4 py-2 rounded-t-lg">
              <span className="text-sm font-medium">Command/Prompt</span>
              <button
                onClick={() => copyCode(quickStartSteps[currentStep].code)}
                className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors"
              >
                {copiedCode === quickStartSteps[currentStep].code ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span className="text-xs">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span className="text-xs">Copy</span>
                  </>
                )}
              </button>
            </div>
            <pre className="bg-gray-900 text-green-400 p-4 rounded-b-lg overflow-x-auto">
              <code>{quickStartSteps[currentStep].code}</code>
            </pre>
          </div>

          {/* Step-specific content */}
          {currentStep === 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Prerequisites:</h4>
              <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-1">
                <li>• VS Code (latest version)</li>
                <li>• GitHub account with Copilot access</li>
                <li>• Node.js (for web projects)</li>
                <li>• Git installed and configured</li>
              </ul>
            </div>
          )}

          {currentStep === 1 && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">Pro Tips:</h4>
              <ul className="text-green-800 dark:text-green-200 text-sm space-y-1">
                <li>• Use Agent mode for complex, multi-file tasks</li>
                <li>• Be specific about your project requirements</li>
                <li>• Include technology preferences in your prompt</li>
                <li>• Let the AI suggest best practices</li>
              </ul>
            </div>
          )}

          {currentStep === 2 && (
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">Development Best Practices:</h4>
              <ul className="text-purple-800 dark:text-purple-200 text-sm space-y-1">
                <li>• Implement one feature at a time</li>
                <li>• Test after each implementation</li>
                <li>• Use the chat to review and improve code</li>
                <li>• Follow established coding patterns</li>
              </ul>
            </div>
          )}

          {currentStep === 3 && (
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
              <h4 className="font-semibold text-orange-900 dark:text-orange-100 mb-2">Deployment Checklist:</h4>
              <ul className="text-orange-800 dark:text-orange-200 text-sm space-y-1">
                <li>• Run all tests and ensure they pass</li>
                <li>• Check for security vulnerabilities</li>
                <li>• Optimize performance and bundle size</li>
                <li>• Update documentation and README</li>
              </ul>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            onClick={prevStep}
            disabled={currentStep === 0}
            variant="outline"
            size="lg"
          >
            Previous
          </Button>

          <div className="text-center">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 font-medium">
              Need help? Check the{' '}
              <a href="/guide" className="text-primary-700 dark:text-primary-400 hover:underline font-semibold">
                complete guide
              </a>
            </p>
          </div>

          {currentStep < quickStartSteps.length - 1 ? (
            <Button
              onClick={nextStep}
              variant="primary"
              size="lg"
            >
              Next Step
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              variant="success"
              size="lg"
              onClick={() => window.location.href = '/examples'}
            >
              View Examples
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {/* Completion Message */}
        {currentStep === quickStartSteps.length - 1 && (
          <div className="mt-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg p-6 text-center text-white">
            <h3 className="text-xl font-bold mb-2">🎉 Congratulations!</h3>
            <p className="text-green-100 mb-4">
              You've completed the quick start guide. You're now ready to build amazing projects with GitHub Copilot Agent!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => window.location.href = '/examples'}
                className="bg-white text-green-700 hover:bg-gray-100 hover:text-green-800"
              >
                Explore Examples
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => window.location.href = '/guide'}
                className="border-white text-white hover:bg-white/10"
              >
                Read Full Guide
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default QuickStart
